import { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Play, Image as ImageIcon, Mail } from 'lucide-react';

/**
 * "Experience a Living Announcement" — auto-advancing 6-beat phone-frame
 * demo so visitors feel the product in under 15 seconds without scrolling
 * through marketing copy.
 *
 * Built from in-app visual language only (no stock imagery).
 */

interface Beat {
  caption: string;
  render: () => React.ReactNode;
}

const BEAT_MS = 2400;

export function ExperienceLivingAnnouncement() {
  const prefersReducedMotion = useReducedMotion();
  const [active, setActive] = useState(0);
  const pausedRef = useRef(false);

  const beats: Beat[] = [
    {
      caption: "1. The notification lands",
      render: () => (
        <div className="absolute inset-0 flex flex-col items-center justify-start pt-16 px-5">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full rounded-2xl bg-white/10 backdrop-blur-lg border border-white/15 p-3 flex items-start gap-3 shadow-2xl"
          >
            <div className="w-9 h-9 rounded-xl bg-[#00C896] flex items-center justify-center shrink-0">
              <Heart className="w-4 h-4 text-[#0D1B1E]" fill="currentColor" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-[#00C896] font-medium">Say It NOW · now</div>
              <div className="text-[13px] font-medium text-[#F4EDE4] mt-0.5 leading-snug">
                A Living Announcement for you
              </div>
              <div className="text-[11px] text-[#F4EDE4]/70 leading-snug mt-0.5">
                From someone who loves you. Tap to open →
              </div>
            </div>
          </motion.div>
        </div>
      ),
    },
    {
      caption: "2. The reveal begins",
      render: () => (
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            initial={{ scale: 1.2, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1.2 }}
            className="absolute inset-0"
            style={{
              background:
                'radial-gradient(ellipse at center, rgba(0,200,150,0.25) 0%, rgba(13,27,30,0.95) 70%)',
            }}
          />
          {/* Floating particles */}
          {Array.from({ length: 18 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 rounded-full bg-[#F4EDE4]/70"
              initial={{
                x: `${(i * 37) % 100}%`,
                y: '100%',
                opacity: 0,
              }}
              animate={{
                y: '-10%',
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 2 + (i % 3),
                delay: i * 0.05,
                repeat: Infinity,
              }}
            />
          ))}
          <div className="absolute inset-0 flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0, letterSpacing: '0.5em' }}
              animate={{ opacity: 1, letterSpacing: '0.15em' }}
              transition={{ duration: 1.5 }}
              className="text-[10px] uppercase tracking-widest text-[#F4EDE4]/80"
            >
              For you, Mom
            </motion.div>
          </div>
        </div>
      ),
    },
    {
      caption: "3. The message arrives",
      render: () => (
        <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-serif italic text-[#F4EDE4] text-[15px] leading-relaxed"
          >
            "You taught me what soft strength looks like.
            Every quiet sacrifice you thought I didn't notice —
            I noticed."
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.7 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-5 text-[10px] uppercase tracking-widest text-[#F4EDE4]/70"
          >
            — Your daughter
          </motion.p>
        </div>
      ),
    },
    {
      caption: "4. Photos come alive",
      render: () => (
        <div className="absolute inset-0 grid grid-cols-2 grid-rows-2 gap-1 p-3">
          {[
            'from-rose-400/60 to-amber-400/40',
            'from-emerald-400/50 to-teal-500/40',
            'from-amber-400/60 to-orange-500/40',
            'from-pink-400/50 to-purple-500/40',
          ].map((g, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: i * 0.12 }}
              className={`relative rounded-lg overflow-hidden bg-gradient-to-br ${g}`}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <ImageIcon className="w-6 h-6 text-white/40" />
              </div>
            </motion.div>
          ))}
        </div>
      ),
    },
    {
      caption: "5. Your voice, on video",
      render: () => (
        <div className="absolute inset-0 p-5 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative w-full aspect-[3/4] rounded-2xl overflow-hidden bg-gradient-to-br from-[#0D1B1E] via-[#1a3a3d] to-[#0D1B1E] border border-[#00C896]/30 shadow-2xl"
          >
            <div className="absolute inset-0 flex items-center justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                className="w-14 h-14 rounded-full bg-[#00C896] flex items-center justify-center shadow-xl"
              >
                <Play className="w-6 h-6 text-[#0D1B1E] ml-0.5" fill="currentColor" />
              </motion.button>
            </div>
            <div className="absolute bottom-3 left-3 right-3 text-[10px] text-[#F4EDE4]/80 text-center">
              A 90-second message · From Sarah
            </div>
          </motion.div>
        </div>
      ),
    },
    {
      caption: "6. She watches it six times",
      render: () => (
        <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: 'spring', stiffness: 120, damping: 14 }}
            className="w-14 h-14 rounded-full bg-[#00C896]/20 border border-[#00C896]/50 flex items-center justify-center mb-4"
          >
            <Heart className="w-6 h-6 text-[#00C896]" fill="currentColor" />
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="font-serif italic text-[#F4EDE4] text-[15px] leading-relaxed"
          >
            "I cried before I finished reading. I've watched the video six times."
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.6 }}
            transition={{ delay: 0.8 }}
            className="mt-4 text-[10px] uppercase tracking-widest text-[#F4EDE4]/70"
          >
            — A real reaction
          </motion.p>
        </div>
      ),
    },
  ];

  // Auto-advance (skipped under reduced-motion)
  useEffect(() => {
    if (prefersReducedMotion) return;
    const id = setInterval(() => {
      if (pausedRef.current) return;
      setActive((a) => (a + 1) % beats.length);
    }, BEAT_MS);
    return () => clearInterval(id);
  }, [prefersReducedMotion, beats.length]);

  return (
    <section
      className="relative py-20 sm:py-28 overflow-hidden"
      aria-label="Experience a Living Announcement"
    >
      <div className="container max-w-6xl mx-auto px-4">
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.25em] text-[#00C896] mb-4 font-medium">
            See it. Feel it.
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif text-foreground mb-4">
            Experience a Living Announcement
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-xl mx-auto">
            Most visitors read about it. Watch what actually happens when someone opens one.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center max-w-4xl mx-auto">
          {/* Phone frame */}
          <div className="flex justify-center">
            <div
              className="relative w-[260px] sm:w-[280px] aspect-[9/19.5] rounded-[2.5rem] bg-[#0D1B1E] p-2 shadow-[0_30px_80px_-20px_rgba(0,200,150,0.35)] border border-white/10"
              onMouseEnter={() => { pausedRef.current = true; }}
              onMouseLeave={() => { pausedRef.current = false; }}
              onTouchStart={() => { pausedRef.current = true; }}
              onTouchEnd={() => { setTimeout(() => { pausedRef.current = false; }, 1500); }}
            >
              {/* Notch */}
              <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-5 rounded-b-2xl bg-[#0D1B1E] z-20" />
              {/* Screen */}
              <div className="relative w-full h-full rounded-[2rem] overflow-hidden bg-gradient-to-b from-[#0D1B1E] via-[#0a1518] to-[#0D1B1E]">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={active}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: prefersReducedMotion ? 0 : 0.5 }}
                    className="absolute inset-0"
                  >
                    {beats[active].render()}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Caption + dots */}
          <div className="text-center md:text-left">
            <AnimatePresence mode="wait">
              <motion.p
                key={active}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.3 }}
                className="text-xl sm:text-2xl font-serif text-foreground mb-6"
              >
                {beats[active].caption}
              </motion.p>
            </AnimatePresence>

            <div className="flex gap-2 justify-center md:justify-start mb-8">
              {beats.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  aria-label={`Go to step ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all ${
                    i === active
                      ? 'w-8 bg-[#00C896]'
                      : 'w-1.5 bg-foreground/20 hover:bg-foreground/40'
                  }`}
                />
              ))}
            </div>

            <Link
              to="/create"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#00C896] text-[#0D1B1E] font-medium hover:shadow-lg transition-all hover:scale-[1.02]"
            >
              Create yours now
              <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="mt-3 text-xs text-muted-foreground">
              First 3 are free · No credit card
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
